import { Component, OnInit, inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { BackButton } from '@components/ui/back-button/back-button';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { SatelliteProductService } from '@6grain/masofa-monolith-api-client/common/api/satelliteProduct.service';
import { GetSatelliteProductsWithIndicesRequest } from '@6grain/masofa-monolith-api-client/common/model/getSatelliteProductsWithIndicesRequest';
import { GetSatelliteProductsWithIndicesResponse } from '@6grain/masofa-monolith-api-client/common/model/getSatelliteProductsWithIndicesResponse';
import { SatelliteProductWithIndicesDto } from '@6grain/masofa-monolith-api-client/common/model/satelliteProductWithIndicesDto';
import { SatelliteProductIndicesDto } from '@6grain/masofa-monolith-api-client/common/model/satelliteProductIndicesDto';
import { SatelliteIndexLayerDto } from '@6grain/masofa-monolith-api-client/common/model/satelliteIndexLayerDto';
import { ProductSourceType } from '@6grain/masofa-monolith-api-client/common/model/productSourceType';
import { PaginatorState } from 'primeng/paginator';
import { environment } from '@root/src/environments/environment';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { MsfTreeSelect } from '@components/tree-select/tree-select';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { ServerTranslateService } from '@app/services/server-translate.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomPaginatorComponent } from '../Ui-Kit/custom-paginator/custom-paginator.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { StatusTag } from '@components/ui/status-tag/status-tag';
import { fileIdOrUndefined } from '@app/shared/helpers/file-id.helper';
import {
  getMaxCloudCoverPayloadValue,
  normalizeCloudCover,
  normalizeMaxCloudCoverInput,
  readTerritoryCloudCover,
} from '@app/utils/cloud-cover.util';

interface SatelliteLayer {
  name: string;
  imageUrl: string | null;
  downloadId: string | null;
  isLoading: boolean;
  hasError: boolean;
}

interface SatelliteRow {
  id: string;
  date: Date;
  satelliteName: string;
  cloudCover: number | null;
  territoryCloudCover: number | null;
  coordinates: {
    lat: number;
    lon: number;
  };
  layers: Record<string, SatelliteLayer>;
  raw?: SatelliteProductWithIndicesDto;
  sizeMb?: number;
  mediadataPath?: string;
  regionIds: string[];
}

@Component({
  selector: 'app-crop-monitoring-satellite',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    BackButton,
    DatePickerComponent,
    CustomPaginatorComponent,
    MsfTreeSelect,
    ServerTranslatePipe,
    PrimaryButton,
    SecondaryButton,
    StatusTag,
  ],
  providers: [DatePipe],
  templateUrl: './crop-monitoring-satellite.component.html',
  styleUrls: ['./crop-monitoring-satellite.component.scss'],
})
export class CropMonitoringSatelliteComponent implements OnInit, OnDestroy {
  private readonly satelliteService = inject(SatelliteProductService);
  public readonly regionManagerService = inject(RegionManagerService);
  private readonly datePipe = inject(DatePipe);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly destroy$ = new Subject<void>();
  private dataSubscription: Subscription | null = null;

  private readonly fileStorageUrl = `${environment.api}/common/FileStorageItem/GetFile`;
  isLoading = false;
  totalCount = 0;

  pageSize = 10;
  first = 0;

  filters = {
    startDate: null,
    endDate: null,
    satelliteName: null,
    regionId: null as string | null,
    maxCloudCover: 100,
  };

  originDateOfLastSatelliteProduct?: Date;
  lastUpdatedAt: Date | null = null;

  layerColumns: string[] = [];
  rows: SatelliteRow[] = [];
  regions: Region[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loadRegions();
    this.loadData();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event: LangChangeEvent) => {
      this.sortAllRowsRegions();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRegions(): void {
    this.regionManagerService.getRegions().then((res) => {
      this.regions = res || [];
      if (this.rows.length > 0) {
        this.sortAllRowsRegions();
      }
    }).catch((err) => {
      console.error('Error loading regions', err);
    });
  }

  private sortAllRowsRegions(): void {
    if (!this.rows || this.rows.length === 0 || !this.regions || this.regions.length === 0) {
      return;
    }

    this.rows.forEach((row) => {
      if (row.regionIds && row.regionIds.length > 1) {
        row.regionIds.sort((idA, idB) => {
          const regionA = this.getRegionById(idA);
          const regionB = this.getRegionById(idB);
          const nameA = regionA ? (this.serverTranslate.translate(regionA, 'names') as string) : '';
          const nameB = regionB ? (this.serverTranslate.translate(regionB, 'names') as string) : '';
          return nameA.localeCompare(nameB);
        });

        const seenNames = new Set<string>();
        row.regionIds = row.regionIds.filter((id) => {
          const region = this.getRegionById(id);
          const name = region ? (this.serverTranslate.translate(region, 'names') as string) : id;
          if (seenNames.has(name)) {
            return false;
          }
          seenNames.add(name);
          return true;
        });
      }
    });
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? 10;
    this.loadData();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.first = 0;
    this.loadData();
  }

  loadData(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
      this.dataSubscription = null;
    }

    this.isLoading = true;
    const offset = this.first;

    let filterStartDate: string | null = null;
    let filterEndDate: string | null = null;

    if (this.filters.startDate) {
      filterStartDate = this.datePipe.transform(this.filters.startDate, 'yyyy-MM-dd');
    }
    if (this.filters.endDate) {
      filterEndDate = this.datePipe.transform(this.filters.endDate, 'yyyy-MM-dd');
    }

    let satelliteType: ProductSourceType | undefined;
    if (this.filters.satelliteName === 'Sentinel') {
      satelliteType = 1 as ProductSourceType;
    } else if (this.filters.satelliteName === 'Landsat') {
      satelliteType = 2 as ProductSourceType;
    }

    const maxCloudCover = getMaxCloudCoverPayloadValue(this.filters.maxCloudCover);
    const query: GetSatelliteProductsWithIndicesRequest = {
      offset: offset,
      take: this.pageSize,
      startDate: filterStartDate,
      endDate: filterEndDate,
      satelliteType: satelliteType,
      regionId: this.filters.regionId,
    };

    if (maxCloudCover !== undefined) {
      query.maxCloudCover = maxCloudCover;
    }

    this.dataSubscription = this.satelliteService.commonSatelliteProductGetWithIndicesPost(query).subscribe({
      next: (response: GetSatelliteProductsWithIndicesResponse) => {
        const data = response.data || [];
        this.updateLayerColumns(data);
        this.rows = data.map((p) => this.mapToRow(p));
        this.sortAllRowsRegions();
        this.totalCount = response.total || 0;
        if (response.originDateOfLastSatelliteProduct != null) {
          this.originDateOfLastSatelliteProduct = new Date(response.originDateOfLastSatelliteProduct);
        }
        this.lastUpdatedAt = this.originDateOfLastSatelliteProduct ?? null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading satellite products', err);
        this.isLoading = false;
      },
    });
  }

  private updateLayerColumns(data: SatelliteProductWithIndicesDto[]): void {
    const uniqueKeys = new Set<string>();
    uniqueKeys.add('rgb');
    data.forEach((item) => {
      if (item.indices) {
        (Object.keys(item.indices) as Array<keyof SatelliteProductIndicesDto>).forEach((key) => {
          if (item.indices?.[key]) {
            uniqueKeys.add(key.toLowerCase());
          }
        });
      }
    });
    this.layerColumns = Array.from(uniqueKeys).sort((a, b) => {
      if (a === 'rgb') return -1;
      if (b === 'rgb') return 1;
      return a.localeCompare(b);
    });
  }

  private mapToRow(product: SatelliteProductWithIndicesDto): SatelliteRow {
    let coords = { lat: 0, lon: 0 };

    if (product.center) {
      coords = {
        lat: product.center.latitude ?? 0,
        lon: product.center.longitude ?? 0,
      };
    }

    const displaySatelliteName = product.mission || this.translate.instant('Unknown');

    return {
      id: product.id || '',
      date: product.originDate ? new Date(product.originDate) : new Date(),
      satelliteName: displaySatelliteName,
      cloudCover: normalizeCloudCover(product.cloudCover),
      territoryCloudCover: readTerritoryCloudCover(product),
      coordinates: coords,
      layers: this.mapLayers(product),
      sizeMb: product.sizeMb || undefined,
      mediadataPath: fileIdOrUndefined(product.mediadataPath),
      raw: product,
      regionIds: Array.from(new Set(product.regionIds || [])),
    };
  }

  private mapLayers(product: SatelliteProductWithIndicesDto): Record<string, SatelliteLayer> {
    const layers: Record<string, SatelliteLayer> = {};

    const createLayer = (
      name: string,
      path: string | null | undefined,
      downloadId: string | null | undefined,
    ): SatelliteLayer => ({
      name: name,
      imageUrl: path ? `${this.fileStorageUrl}/${path}` : null,
      downloadId: downloadId || null,
      isLoading: !!path,
      hasError: false,
    });

    layers['rgb'] = createLayer('RGB', product.previewImagePath, null);

    if (product.indices) {
      const indices = product.indices;
      (Object.keys(indices) as Array<keyof SatelliteProductIndicesDto>).forEach((key) => {
        const indexData: SatelliteIndexLayerDto | undefined = indices[key];
        const layerKey = key.toLowerCase();

        if (indexData) {
          layers[layerKey] = createLayer(key.toUpperCase(), indexData.previewImagePath, indexData.fileStorageItemId);
        }
      });
    }
    return layers;
  }

  applyFilters(): void {
    this.first = 0;
    this.loadData();
  }

  resetFilters(): void {
    this.filters = {
      startDate: null,
      endDate: null,
      satelliteName: null,
      regionId: null,
      maxCloudCover: 100,
    };
    this.first = 0;
    this.loadData();
  }

  onMaxCloudCoverChange(value: unknown): void {
    this.filters.maxCloudCover = normalizeMaxCloudCoverInput(value);
  }

  private downloadFile(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_self';
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onDownloadLayer(layer: SatelliteLayer): void {
    if (layer.downloadId) {
      const url = `${this.fileStorageUrl}/${layer.downloadId}`;
      this.downloadFile(url);
    }
  }

  onDownloadArchive(row: SatelliteRow): void {
    if (row.mediadataPath) {
      const url = `${this.fileStorageUrl}/${row.mediadataPath}`;
      this.downloadFile(url);
    }
  }

  getRegionById(id: string): Region | undefined {
    return this.regions.find((r) => r.id === id);
  }

  getRegionsTooltip(regionIds: string[]): string {
    if (!regionIds || !regionIds.length || !this.regions.length) {
      return '';
    }
    return regionIds
      .map((id) => {
        const region = this.getRegionById(id);
        return region ? (this.serverTranslate.translate(region, 'names') as string) : '';
      })
      .filter((name) => !!name)
      .join(', ');
  }
}
